import styled, { css, createGlobalStyle } from 'styled-components';
import { resetBtn } from 'styled-manila';
import { getColor, Fonts, spacingUnits } from 'core/utils';

export const FIXED_WIDTH_MAX = '1320px';

export const CategoryContainer = styled.div`
	font-family: ${Fonts.serif};
	width: 100%;
	height: 48px;
	max-width: ${FIXED_WIDTH_MAX};
	margin: auto;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-around;
	font-size: 14px;
`;

// Used internally below to get the same styles for links & "More"
const NavCategoryContainer = styled.div`
	letter-spacing: 0.12px;
	display: flex;
	align-self: stretch;
	flex-direction: column;
	justify-content: center;
	margin: 0;
`;

export const NavCategoryMoreContainer = styled(NavCategoryContainer)`
	/* caret from bootstrap/dropdowns.less */
	.caret {
		border-top-color: getColor('gray');
		margin-left: 3px;
	}

	a {
		padding: ${spacingUnits(1/2)} ${spacingUnits(1)};
	}

	button, a {
		text-decoration: none;
		color: getColor('gray');
		background: none;
		border-width: 0;

		&:hover, &:focus, &:active {
			text-decoration: none;
			color: ${getColor('darkTeal')};
		}
	}
`;

export const NavCategoryLink = styled(NavCategoryContainer).attrs({
	as: "a"
})`
	${({ highlight, accentColor }) => highlight && css`
		border-bottom: 2px solid ${accentColor};
	`}

	${({ accentColor }) => accentColor && css`
		&:hover, &:active {
			border-bottom: 2px solid ${accentColor};
		}
	`}
`;

/**
 * Output media queries to hide full text at breakpoint & show short text
 *
 * @param {str} slug - cooresponding to `data-slug` on `NavCategoryLink`
 * @param {int} bp - breakpoint at which to swap full for short text
 */
const hideFullTextBp = (slug, bp) => css`
	@media (max-width: ${bp - 1}px) {
		${NavCategoryLink}[data-slug="${slug}"] & {
			${({ shortText }) => shortText && css`
				display: inherit;
			`}
			${({ fullText }) => fullText && css`
				display: none;
			`}
		}
	}
`;

export const CategorySpan = styled.span`
	${resetBtn};
	display: flex;
	align-items: center;
	white-space: nowrap;
	overflow: hidden;
	color: ${props => props.linkColor ? props.linkColor : getColor('gray')};
	transition: color .2s ease-in-out;
	font-weight: ${props => props.catType === 'campaign' ? 600 : 400};
	&:hover {
		color: ${getColor('gray')};
	}
	&:focus {
		border: 1px dotted ${getColor('darkTeal')};
	}

	/* small text rules */
	${({ shortText }) => shortText && css`
		display: none;
	`}

	> svg {
		min-width: 24px;
		flex-basis: 24px;
	}

	${hideFullTextBp("family-kids-subscription-boxes", 810)}
	${hideFullTextBp("collections", 1030)}
	${hideFullTextBp("food-subscription-boxes", 1070)}
	${hideFullTextBp("book-subscription-boxes", 1160)}
	${hideFullTextBp("home-garden-subscription-boxes", 1250)}
	${hideFullTextBp("spiritual-subscription-boxes", 1310)}
`;

export const CollapsedItemsDropdown = styled.ul`
	margin-top: 0;
	right: 0;
	left: inherit;
	padding: ${spacingUnits(1/2)} 0;
	border: none;
`;

/**
 * Output media queries to move menu item into dropdown at breakpoint
 *
 * @param {str} slug - cooresponding to `data-slug` on `NavCategoryLink`
 * @param {int} bp - breakpoint at which to hide item in "More" dropdown
 */
const showMainNavLink = (slug, bp) => css`
	@media (min-width: ${bp}px) {
		${NavCategoryLink} {
			&[data-slug="${slug}"] { display: inherit; }
		}
		${CollapsedItemsDropdown} {
			[data-slug="${slug}"] { display: none; }
		}
	}
`;

const SHOW_MORE_DROPDOWN_WIDTH = 1110; // in pixels
export const NavWidthMediaQueries = createGlobalStyle`${css`
	/* menu collapse rules: */
	${NavCategoryLink} {
		&[data-slug="spiritual-subscription-boxes"],
		&[data-slug="home-garden-subscription-boxes"],
		&[data-slug="novelty-subscription-boxes"],
		&[data-slug="unique-subscription-boxes"]
		{ display: none; }
	}

	@media (min-width: ${SHOW_MORE_DROPDOWN_WIDTH}px) {
		${NavCategoryMoreContainer} {
			display: none;
		}
	}

	${showMainNavLink("spiritual-subscription-boxes", 870)}
	${showMainNavLink("home-garden-subscription-boxes", 940)}
	${showMainNavLink("novelty-subscription-boxes", SHOW_MORE_DROPDOWN_WIDTH)}
	${showMainNavLink("unique-subscription-boxes", SHOW_MORE_DROPDOWN_WIDTH)}
`}`;
